// AudioSynthHelper.ts - Shared Web Audio API synthesis primitives and helpers

export interface ToneOptions {
  freq: number;
  endFreq?: number;
  start?: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  attack?: number;
  endGain?: number;
  filter?: {
    type: BiquadFilterType;
    freq: number;
    endFreq?: number;
    Q?: number;
  };
}

export interface DualToneOptions extends ToneOptions {
  detuneMultiplier?: number;
}

export interface NoiseBurstOptions {
  start?: number;
  dur: number;
  decay?: number;
  gain?: number;
  attack?: number;
  endGain?: number;
  filter?: {
    type: BiquadFilterType;
    freq: number;
    endFreq?: number;
    Q?: number;
  };
}

export class AudioSynthHelper {
  private static sharedNoiseBuffer: AudioBuffer | null = null;

  private static getSharedNoiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.sharedNoiseBuffer && this.sharedNoiseBuffer.sampleRate === ctx.sampleRate) {
      return this.sharedNoiseBuffer;
    }
    const sampleRate = ctx.sampleRate || 48000;
    const buffer = ctx.createBuffer(1, sampleRate, sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < sampleRate; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    this.sharedNoiseBuffer = buffer;
    return buffer;
  }

  /**
   * Returns the designated master destination bus or context destination.
   */
  public static getDestination(ctx: AudioContext): AudioNode {
    return (ctx as any).__masterNode || ctx.destination;
  }

  /**
   * Synthesize a single tone with frequency ramping, gain envelope, and optional biquad filtering.
   */
  public static playTone(ctx: AudioContext, opts: ToneOptions) {
    const t = opts.start !== undefined ? Math.max(opts.start, ctx.currentTime) : ctx.currentTime;
    const dur = opts.dur;
    const attack = opts.attack ?? 0.004;
    const peakGain = opts.gain ?? 0.2;
    const endGain = opts.endGain ?? 0.0001;

    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    let filterNode: BiquadFilterNode | null = null;

    osc.type = opts.type || 'sine';
    osc.frequency.setValueAtTime(opts.freq, t);
    if (opts.endFreq !== undefined && opts.endFreq > 0) {
      osc.frequency.exponentialRampToValueAtTime(opts.endFreq, t + dur);
    }

    gainNode.gain.setValueAtTime(0.0001, t);
    gainNode.gain.linearRampToValueAtTime(peakGain, t + attack);
    gainNode.gain.exponentialRampToValueAtTime(endGain, t + dur);

    if (opts.filter) {
      filterNode = ctx.createBiquadFilter();
      filterNode.type = opts.filter.type;
      filterNode.frequency.setValueAtTime(opts.filter.freq, t);
      if (opts.filter.endFreq !== undefined && opts.filter.endFreq > 0) {
        filterNode.frequency.exponentialRampToValueAtTime(opts.filter.endFreq, t + dur);
      }
      if (opts.filter.Q !== undefined) {
        filterNode.Q.setValueAtTime(opts.filter.Q, t);
      }
      osc.connect(filterNode);
      filterNode.connect(gainNode);
    } else {
      osc.connect(gainNode);
    }

    gainNode.connect(this.getDestination(ctx));

    osc.onended = () => {
      try {
        osc.disconnect();
        filterNode?.disconnect();
        gainNode.disconnect();
      } catch {}
    };

    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  /**
   * Synthesize a rich double-oscillator tone (e.g. Brass fanfare chords or chorus pads)
   */
  public static playDualTone(ctx: AudioContext, opts: DualToneOptions) {
    const t = opts.start !== undefined ? Math.max(opts.start, ctx.currentTime) : ctx.currentTime;
    const dur = opts.dur;
    const attack = opts.attack ?? 0.02;
    const peakGain = opts.gain ?? 0.28;
    const endGain = opts.endGain ?? 0.0001;
    const detuneMul = opts.detuneMultiplier ?? 1.004;

    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gainNode = ctx.createGain();
    let filterNode: BiquadFilterNode | null = null;

    osc1.type = opts.type || 'sawtooth';
    osc2.type = opts.type || 'sawtooth';

    osc1.frequency.setValueAtTime(opts.freq, t);
    osc2.frequency.setValueAtTime(opts.freq * detuneMul, t);

    if (opts.endFreq !== undefined && opts.endFreq > 0) {
      osc1.frequency.exponentialRampToValueAtTime(opts.endFreq, t + dur);
      osc2.frequency.exponentialRampToValueAtTime(opts.endFreq * detuneMul, t + dur);
    }

    gainNode.gain.setValueAtTime(0.0001, t);
    gainNode.gain.linearRampToValueAtTime(peakGain, t + attack);
    gainNode.gain.setValueAtTime(peakGain * 0.85, t + dur * 0.5);
    gainNode.gain.exponentialRampToValueAtTime(endGain, t + dur);

    if (opts.filter) {
      filterNode = ctx.createBiquadFilter();
      filterNode.type = opts.filter.type;
      filterNode.frequency.setValueAtTime(opts.filter.freq, t);
      if (opts.filter.endFreq !== undefined && opts.filter.endFreq > 0) {
        filterNode.frequency.exponentialRampToValueAtTime(opts.filter.endFreq, t + dur);
      }
      osc1.connect(filterNode);
      osc2.connect(filterNode);
      filterNode.connect(gainNode);
    } else {
      osc1.connect(gainNode);
      osc2.connect(gainNode);
    }

    gainNode.connect(this.getDestination(ctx));

    let endedCount = 0;
    const cleanup = () => {
      endedCount++;
      if (endedCount >= 2) {
        try {
          osc1.disconnect();
          osc2.disconnect();
          filterNode?.disconnect();
          gainNode.disconnect();
        } catch {}
      }
    };
    osc1.onended = cleanup;
    osc2.onended = cleanup;

    osc1.start(t);
    osc2.start(t);
    osc1.stop(t + dur + 0.05);
    osc2.stop(t + dur + 0.05);
  }

  /**
   * Synthesize shaped white noise bursts with exponential decay (e.g. Card slide, flick, cheer applause)
   */
  public static playNoiseBurst(ctx: AudioContext, opts: NoiseBurstOptions) {
    const t = opts.start !== undefined ? Math.max(opts.start, ctx.currentTime) : ctx.currentTime;
    const dur = opts.dur;
    const peakGain = opts.gain ?? 0.18;
    const attack = opts.attack ?? 0.004;
    const endGain = opts.endGain ?? 0.0001;

    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = this.getSharedNoiseBuffer(ctx);
    noiseSource.loop = true;

    const gainNode = ctx.createGain();
    let filterNode: BiquadFilterNode | null = null;

    gainNode.gain.setValueAtTime(0.0001, t);
    gainNode.gain.linearRampToValueAtTime(peakGain, t + attack);
    gainNode.gain.exponentialRampToValueAtTime(endGain, t + dur);

    if (opts.filter) {
      filterNode = ctx.createBiquadFilter();
      filterNode.type = opts.filter.type;
      filterNode.frequency.setValueAtTime(opts.filter.freq, t);
      if (opts.filter.endFreq !== undefined && opts.filter.endFreq > 0) {
        filterNode.frequency.exponentialRampToValueAtTime(opts.filter.endFreq, t + dur);
      }
      if (opts.filter.Q !== undefined) {
        filterNode.Q.setValueAtTime(opts.filter.Q, t);
      }
      noiseSource.connect(filterNode);
      filterNode.connect(gainNode);
    } else {
      noiseSource.connect(gainNode);
    }

    gainNode.connect(this.getDestination(ctx));

    noiseSource.onended = () => {
      try {
        noiseSource.disconnect();
        filterNode?.disconnect();
        gainNode.disconnect();
      } catch {}
    };

    noiseSource.start(t);
    noiseSource.stop(t + dur + 0.05);
  }

  /**
   * Play a sequence / chord of notes sequentially or in parallel
   */
  public static playNoteSequence(
    ctx: AudioContext,
    notes: Array<{
      freq: number;
      start: number;
      dur: number;
      gain?: number;
      type?: OscillatorType;
      filter?: { type: BiquadFilterType; freq: number; endFreq?: number; Q?: number };
    }>,
    baseTime?: number
  ) {
    const t = baseTime !== undefined ? baseTime : ctx.currentTime;
    notes.forEach((note) => {
      this.playTone(ctx, {
        freq: note.freq,
        start: t + note.start,
        dur: note.dur,
        gain: note.gain,
        type: note.type,
        filter: note.filter,
      });
    });
  }
}
